import React from 'react'
import styles from './InvoiceDetails.module.scss'
import ProcessFeeDetails, { ProcessFeeTabType } from '../../components/InvoiceDetailsPage/ProcessFeeDetails';
import Layout from '../../components/core/Layout';
import { useParams } from 'react-router-dom';
import { getProcessFeeDetails, getProcessFeeOptions, postProcessFee, postProcessFeeOptions, getTaxRate } from '../../apis/invoice-api';
import { ProcessFeeDetailsResponseData, ProcessFeeOption } from '../../apis/models/ProcessFeeDetailsResponse';
import { FILE_TYPES, INVOICE_TAB_INDEX, InvStatus } from '../../libs/constants';
import { returnProcessFeeTotalAmount, str2number } from '../../libs/helpers';
import { InvoiceMasterData } from "../../apis/models/InvoiceMasterRequest";
import { ProcessFeeOptionRequestItem, ProcessFeeRequestItem } from '../../apis/models/ProcessFeeRequest';
import { error, success } from '../../apis/reducers/snackbarsReducer';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { getInvoiceMaster, putInvoiceMaster } from '../../apis/invmaster-api';
import { TaxResponseData } from '../../apis/models/TaxResponse';
import DateWithoutTime from '../../libs/dateWithoutTime';
import InvoiceHeader from '../../components/InvoiceDetailsPage/header/InvoiceHeader';
import { RootState } from '../../apis/store';
import { saveMasterInfo } from '../../apis/reducers/invoiceMasterReducer';

const compareAsc = (a: ProcessFeeTabType, b: ProcessFeeTabType) => {
  return DateWithoutTime.sortAsc(a.dateAdded, b.dateAdded);
}

const compareDesc = (a: ProcessFeeTabType, b: ProcessFeeTabType) => {
  return DateWithoutTime.sortAsc(a.dateAdded, b.dateAdded) * -1;
}

const InvoiceProcessFeePage = () => {
  const invoiceMasterState = useSelector((state: RootState) => state.invoiceMaster);
  const dispatch = useDispatch();
  const { id } = useParams();
  const [invoiceMasterData, setInvoiceMasterData] = React.useState<InvoiceMasterData | null>(invoiceMasterState.masterData);
  const [taxItem, setTaxItem] = React.useState<TaxResponseData | null>(null);
  const [processFeeOptions, setProcessFeeOptions] = React.useState<ProcessFeeOption[]>([]);
  const [processFeeDetails, setProcessFeeDetails] = React.useState<ProcessFeeTabType[]>([]);
  const [notYetSaved, setNotYetSaved] = React.useState<boolean>(false);
  const [isChanged, setIsChanged] = React.useState<boolean>(false);
  const [sort, setSort] = React.useState<"dateAsc" | "dateDesc">("dateDesc");

  React.useEffect(() => {
    if (id) {
      loadAllData();
    }
  },[id])

  const hasError = React.useMemo(() => {
    return processFeeDetails.some(fee => {
      return fee.error.dateAdded || fee.error.quantity || fee.error.workDetails
    })
  },[processFeeDetails]);

  const loadAllData = () => {
    getInvoiceMasterData();
    getInvProcessFeeDetails();
    setNotYetSaved(false);
  }

  const onSaveAndCommit = async () => {
    const isSuccess = await saveProcessFeeDetails();
    if (isSuccess){
      onChangeInvoiceStatus(InvStatus.CONFIRMED)
    }
  }

  const onChangeInvoiceStatus = async (status: InvStatus) => {
    try {
      await putInvoiceMaster(id!, {status: status})
      dispatch(success("請求ステータスを変更しました"));
      loadAllData();
    }catch (err) {
      dispatch(error("請求ステータスの変更に失敗しました"));
    }
  }

  const getInvoiceMasterData = () => {
    getInvoiceMaster(id)
    .then(function (response) {
        setInvoiceMasterData(response.data.data);
        dispatch(saveMasterInfo({masterData: response.data.data}))
        getTaxRate(new Date(response.data.data.from).toString())
          .then(res => setTaxItem(res.data.data))
          .catch(err => console.log(err))
    })
    .catch(function (error) {
        console.log(error);
    })
  }

  const getInvProcessFeeDetails = () => {
    if (id !== undefined) {
      const promises = [getProcessFeeOptions(id), getProcessFeeDetails(id)]
      Promise.all(promises)
        .then((values) => {
          setProcessFeeOptions(values[0].data.data)
          setProcessFeeDetails(convertResponseToTabType(values[1].data.data));
        })
        .catch((err) => {})
    }
  }

  const convertResponseToTabType = (datas: ProcessFeeDetailsResponseData[]): ProcessFeeTabType[] => {
    const results = datas.map((fee: ProcessFeeDetailsResponseData) => {
      return {
        uniqueId: fee.processFeeId,
        dateAdded: new DateWithoutTime(fee.dateAdded),
        productNumber: fee.productNumber,
        quantity: String(fee.quantity),
        unitPrice: String(fee.processFeeOption.unitPrice),
        workDetailOption: fee.processFeeOption,
        error: {}
      } as ProcessFeeTabType;
    });
    results.sort(sort === "dateAsc" ? compareAsc : compareDesc);
    return results;
  }

  const onChangeFees = (items: ProcessFeeTabType[]) => {
    setProcessFeeDetails(items);
    setNotYetSaved(true);
  }

  const onSortDate = () => {
    const afterSort = sort === "dateAsc" ? "dateDesc" : "dateAsc";
    setSort(afterSort);
    processFeeDetails.sort(afterSort === "dateAsc" ? compareAsc : compareDesc);
  }

  const addProcessFeeOption = async (addItem: ProcessFeeOptionRequestItem) => {
    if (!processFeeOptions.every(item => item.processFeeOption.workDetails !== addItem.workDetails)) {
      return
    }
    const reqItems = processFeeOptions.map(o => {
      const option: ProcessFeeOptionRequestItem = {
        id: o.processFeeOptionId,
        unitPrice: o.processFeeOption.unitPrice,
        workDetails: o.processFeeOption.workDetails,
        order: o.order!,
      }
      return option;
    })
    addItem.order = processFeeOptions.reduce((prev, o) => Math.max(prev, o.order ?? 0), 0) + 1;
    reqItems.push(addItem);
    
    await postProcessFeeOptions(id!, {processFeeOptions: reqItems})
    const options = await getInvoiceProcessFeeOptions(id!);
    setProcessFeeOptions(options)
  }

  async function getInvoiceProcessFeeOptions(invoiceMasterId: string): Promise<ProcessFeeOption[]> {
    try {
      const response = await getProcessFeeOptions(invoiceMasterId);
      return response.data.data;
    } catch (error) {
      return [];
    }
  }
 
  const saveProcessFeeDetails = async (): Promise<boolean> => {
    if (!id) {
      return false
    }
    const req: ProcessFeeRequestItem[] = [];
    processFeeDetails.forEach(item => {
      const data: ProcessFeeRequestItem ={
        dateAdded: item.dateAdded.getValue4Server(),
        invoiceMasterId: id!,
        processFeeOptionId: item.workDetailOption?.processFeeOptionId ?? "no-option",
        productCode: item.productNumber,
        quantity: str2number(item.quantity),
      }
      req.push(data);
    })
    try {
      await postProcessFee(id, req)
      dispatch(success("保存しました"));
      loadAllData();

      return true;
    } catch (e) {
      dispatch(error("保存に失敗しました"))
    }
    return false;
  }

  const handleDownloadProcessFee = async () => {
    const response = await getProcessFeeDetails(id!);
    const outputData: ProcessFeeDetailsResponseData[] = response.data.data;

    const ExcelJS = require('exceljs');
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Sheet1');
    const rows: any[] = [];
    outputData.forEach(item => {
      const row = [format(new Date(item.dateAdded), 'yyyy/MM/dd'), item.productNumber, item.processFeeOption.workDetails, item.processFeeOption.unitPrice, item.quantity, item.processFeeOption.unitPrice * item.quantity];
      rows.unshift(row);
    })
    rows.unshift(["日付", "品番", "作業内容", "単価", "枚数", "作業費"]);
    rows.push(["", "", "", "", "合計金額",returnProcessFeeTotalAmount(outputData)])
    rows.push({date: new Date(), productname: 'product', workdetails: "details", up: 0, qty: 0, amount: 0})
    sheet.addRows(rows);
    workbook.xlsx.writeBuffer().then(function (data: any) {
      const blob = new Blob([data], {type: FILE_TYPES.xlsx});
      const fileName = "ProcessFee"
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    });
  }

  return (
    <Layout withBreadcrumbs={true} bgColor="#FAFAFA">
      <React.Fragment>
        <div className={styles.Gray}>
          <div>
            <InvoiceHeader
              invoiceMaster={invoiceMasterData ?? undefined}
              tax={taxItem ?? undefined}
              selectedTab={INVOICE_TAB_INDEX.PROCESS}
              needSave={notYetSaved}
              error={hasError}
              onSaveAndCommit={() => onSaveAndCommit()}
              onChangeStatus={(status) => onChangeInvoiceStatus(status)}
              onMasterChange={() => loadAllData()}
            />
          </div>
          <div  className={styles.tabs}>
            <div className={styles.tabs__body}>
              {invoiceMasterData &&
                <ProcessFeeDetails
                  targetInvoice={invoiceMasterData}
                  tabValues={processFeeDetails}
                  processFeeOptions={processFeeOptions}
                  isChanged={isChanged}
                  onDownloadReport={() => handleDownloadProcessFee()}
                  onNotYetConfirmed={() => onChangeInvoiceStatus(InvStatus.NOT_CONFIRMED)}
                  onReset={() => loadAllData()}
                  onSave={() => saveProcessFeeDetails()}
                  onChange={(items) => {onChangeFees(items); setIsChanged(true)}}
                  onClickSort={() => onSortDate()}
                  onAddOptions={(val) => addProcessFeeOption(val)}
                />
              }
            </div>
          </div>
        </div>
      </React.Fragment>
    </Layout>
  )
}

export default InvoiceProcessFeePage